import { toast } from 'react-toastify';

import { CsvDataRow, TotemCsvInputButton } from 'components/TotemCsvInputButton';
import { TotemSelect } from 'components/TotemSelect';

import styled from 'styled-components';
import { ProductArrangementFormValues, ProductArrangementProductWithStockMaxFormValues } from './FormHelper/DataMapper';

import { GetAllProductsQuery } from 'data/__generated__';

export const ProductArrangementProductSelector = ({
    productArrangementFormValues,
    setProductArrangementFormValuesAndIsFormDirty,
    products,
}: {
    productArrangementFormValues: ProductArrangementFormValues;
    setProductArrangementFormValuesAndIsFormDirty: ({
        newProductArrangementFormValues,
        newIsDirty,
    }: {
        newProductArrangementFormValues: ProductArrangementFormValues;
        newIsDirty?: boolean | undefined;
    }) => void;
    products: GetAllProductsQuery['getAllProducts'];
}) => {
    const productsOptions =
        products
            .map(({ _id, name, brand, volume }) => ({
                value: _id,
                label: `${name} - ${brand} - ${volume}`,
            }))
            .sort((a, b) => a.label.localeCompare(b.label)) || [];

    function getProductArrangementFormValuesProductWithStockMaxFromId(
        productId: string,
        stockMax?: number,
    ): ProductArrangementProductWithStockMaxFormValues | null {
        const productForProductArrangement = products.find(({ _id }) => _id === productId);
        if (!productForProductArrangement) {
            return null;
        }

        const {
            _id,
            brand,
            fullname,
            height,
            imageUrl,
            isDoubleCoil,
            isMicrostore,
            length,
            name,
            state,
            volume,
            weight,
            width,
            isToBeArchived,
            isWithoutStock,
        } = productForProductArrangement;
        return {
            product: {
                _id,
                brand,
                fullname,
                height,
                imageUrl,
                isDoubleCoil,
                isMicrostore,
                length,
                name,
                state,
                volume,
                weight,
                width,
                isToBeArchived,
                isWithoutStock,
            },
            stockMax: stockMax ?? null,
        };
    }

    function onSelect(value: string | null) {
        if (!value) {
            return;
        }
        const newProductWithStockMax = getProductArrangementFormValuesProductWithStockMaxFromId(value);
        if (
            newProductWithStockMax &&
            !productArrangementFormValues.productsWithStockMax.some(
                ({ product }) => product._id === newProductWithStockMax.product._id,
            )
        ) {
            setProductArrangementFormValuesAndIsFormDirty({
                newProductArrangementFormValues: {
                    ...productArrangementFormValues,
                    productsWithStockMax: [
                        newProductWithStockMax,
                        ...productArrangementFormValues.productsWithStockMax,
                    ],
                },
            });
        }
    }

    function handleCSVImport(csvData: CsvDataRow[]) {
        const newProductsWithStockMax: ProductArrangementProductWithStockMaxFormValues[] = [];

        const [header, ...rawData] = csvData;
        const data = rawData.filter((row) => {
            return row.some((cell) => cell.trim() !== '');
        });
        const productIdIndex = header.findIndex((elem) => elem === 'productId');
        const stockMaxIndex = header.findIndex((elem) => elem === 'stockMax');

        const numberOfFields = header.length;

        if (!data.length) {
            toast.error('Veuillez vérifier le fichier CSV. Il semble être vide');
            return;
        }

        //if extra line in 2 column file, corrups all data, last line - ['']
        if (data[data.length - 1].length === 1 && data[data.length - 1][0] === '') {
            toast.error(
                "Fichier CSV mal formaté. Veuillez vérifier que vous n'avez pas laissé de lignes ou de colonnes vides",
            );
            return;
        }

        if (productIdIndex === -1 || stockMaxIndex === -1) {
            toast.error("Veuillez vérifier les champs CSV. Il devrait y avoir 'productId' et 'stockMax'");
            return;
        }

        data.forEach((productArrayFromCSV: Array<string>, rowIndex: number): void => {
            if (productArrayFromCSV.length !== numberOfFields) {
                toast.error(
                    `Veuillez vérifier le fichier CSV. Mauvais nombre de colonnes sur la ligne ${rowIndex + 2}`,
                );
                return;
            }
            productArrayFromCSV.forEach((field: string, fieldIndex: number) => {
                if (!field) {
                    toast.error(
                        `Veuillez vérifier le fichier CSV. Problème de champs sur la ligne ${rowIndex + 2}, colonne ${
                            fieldIndex + 1
                        }`,
                    );
                    return;
                }
            });
            const productId = productArrayFromCSV[productIdIndex];
            const stockMax = Number(productArrayFromCSV[stockMaxIndex]);
            if (Number.isNaN(stockMax) || !Number.isInteger(stockMax)) {
                toast.error(
                    `Veuillez vérifier le fichier CSV. Quantité stockMax non valide sur la ligne ${rowIndex + 2}`,
                );
                return;
            }
            if (
                newProductsWithStockMax.find(
                    ({ product: alreadyAddedProduct }) => productId === alreadyAddedProduct._id,
                )
            ) {
                toast.error(
                    `Vous avez rajouté plusieurs fois le même id de produit ${productId} sur la ligne ${rowIndex + 2}`,
                );
                return;
            }

            const newProductWithStockMax = getProductArrangementFormValuesProductWithStockMaxFromId(
                productId,
                stockMax,
            );
            if (newProductWithStockMax) {
                newProductsWithStockMax.push(newProductWithStockMax);
            }
        });

        setProductArrangementFormValuesAndIsFormDirty({
            newProductArrangementFormValues: {
                ...productArrangementFormValues,
                productsWithStockMax: newProductsWithStockMax,
            },
        });

        toast.success(`Import réussi de ${newProductsWithStockMax.length} produits`);
    }

    // Todo use react virtualized or react window with select. Because there are too much products
    return (
        <Container>
            <SelectContainer>
                <TotemSelect
                    label="Ajouter un produit"
                    placeholder="Sélectionner un produit (recherche par nom ou par _id)"
                    value={null}
                    options={productsOptions}
                    onChange={onSelect}
                />
            </SelectContainer>

            <ButtonContainer>
                <TotemCsvInputButton onCsvDataUpload={handleCSVImport} />
            </ButtonContainer>
        </Container>
    );
};

const Container = styled.span`
    display: flex;
    flex-direction: row;
    align-items: flex-end;
    width: 100%;
`;

const SelectContainer = styled.div`
    flex-grow: 1;
`;

const ButtonContainer = styled.div`
    margin-left: 20px;
`;
